import { EnvironmentOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Select, SelectProps } from "antd";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { publicAxiosInstance } from "@/axiosInstance";

const SEARCHBOX_API_KEY =
  "pk.eyJ1Ijoic3Nsb3VyZW5jbyIsImEiOiJjbHl6aTFxeWIyZ3N2MmtxbGkxamNkaW5jIn0.x6K4xkVKZoo9W6ij4qYzlw";

export interface MapboxSuggest {
  suggestions: {
    full_address: string;
    name: string;
    place_formatted: string;
  }[];
}

export function useSearchbox() {
  return useMutation({
    mutationFn: (value: string) =>
      publicAxiosInstance.get<MapboxSuggest>("https://api.mapbox.com/search/searchbox/v1/suggest", {
        params: {
          q: value,
          types: "address,city,country",
          access_token: SEARCHBOX_API_KEY,
          session_token: "12345",
        },
      }),
  });
}
interface LocationSearchProps extends SelectProps {
  onFocus?: () => void;
}

export default function LocationSearch({ value, onFocus, ...props }: LocationSearchProps) {
  const searchBox = useSearchbox();
  const [hasSearched, setHasSearched] = useState(false);

  const [options, setOptions] = useState<LocationSearchProps["options"]>();

  const search = useCallback((value: string) => {
    setHasSearched(true);

    searchBox
      .mutateAsync(value)
      .then((result) =>
        setOptions(
          result.data.suggestions.map((x) => ({
            label: [x.name, x.place_formatted].filter((x) => x).join(", "),
            value: [x.name, x.place_formatted].filter((x) => x).join(", "),
          }))
        )
      )
      .catch(() => {});
  }, []);

  const debouncedSearch = useMemo(() => _.debounce(search, 250), [search]);

  useEffect(() => {
    setOptions(value ? [{ label: value, value: value }] : []);
  }, [value]);

  return (
    <>
      <Select
        {...props}
        value={value ? value : undefined}
        showSearch
        options={options}
        allowClear={true}
        placeholder={
          <span>
            <EnvironmentOutlined /> Search for a country, city or address
          </span>
        }
        notFoundContent={hasSearched ? "No options found" : ""}
        labelRender={(value) => (
          <span>
            <EnvironmentOutlined /> {value.label}
          </span>
        )}
        filterOption={() => true}
        onFocus={() => {
          value && debouncedSearch(value);
          onFocus?.();
        }}
        onSearch={(searchValue) => searchValue && debouncedSearch(searchValue)}
      />
    </>
  );
}
